import { IAllIssueColumnStates, ISelectedGisMap } from "@mctypes/frontend.types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IAllFilter } from "@schemas/commonSchema";
import { IIssueViewEnum } from "@mcapps/shared";
import { ISortColumn, IStoredIssueColConfigs } from "@schemas/issueSchema";
import { ICapitalLetter, IOtherAttributesMathFormula } from "@schemas/otherAttributesSchema";
import { addAppModalThunk } from "../../../global/globalSlice";
import { IssueFieldEnum, IssueViewEnum } from "@mcapps/shared/enums";
import { AppThunk } from "../../../store";
import { isDeepEqual } from "../../utils";
import { ISSUE_COLUMN_LS_KEY, ISSUE_VIEW_LS_KEY, IssueKanbanGroupingViewEnum } from "./issueConstants";
import { selectGetOpenIssueArgsModalSelector } from "./issueSelectors";

export type IssueDataFieldsObject = {
  [key in keyof typeof IssueFieldEnum]: boolean;
};

export type IIssueDropdownFilters = {
  [key in IssueFieldEnum]?: string[];
};

export interface IssueReducer {
  allFiltersIssueBoardId: string | null;
  selectedPnsFilterItem: string; // 1.1.1
  selectedPnsBoardFilterItem: string; // 1.1.1
  activeTableTemplateId: string | null;
  activeView: IIssueViewEnum;
  kanbanGroupingView: IssueKanbanGroupingViewEnum;
  selectedGisMap: ISelectedGisMap;
  allColumnState: IAllIssueColumnStates;
  sortedCol: ISortColumn;
  activeDashboard: string;
  currentIssueBoardId: string | null; // Added to keep track of current issue board ID
  selectedIssueIdsByBoardId: Record<string, string[]>;
  svarGant: {
    zoomLevel: "week" | "month";
    startDate: string | null;
    endDate: string | null;
    skipItemsWithoutEndDate: boolean;
  };
  dropdownFilters: IIssueDropdownFilters;
  miniSearchFreeText: string;
  issueAskAiPanelOpen: boolean;
  taskAndOrIssueArgs: ("TASK" | "ISSUE")[];
  includeAllCompletedIssuesOnBoard: boolean;
  issueLogsCreatedAtRange: [string | null, string | null];
  issueLogsPageSize: number;
  issueLogsCurrentPage: number;
  mathFormulaConfigDraft: IOtherAttributesMathFormula | null;
  unlockedConnectedTaskCellKeys: string[];
  formulaCounterGroupIssuesModal: {
    issueId: string;
    formulaAttributeKey: ICapitalLetter;
  } | null;
}

// Helper functions to get initial dates
const getInitStartDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0]; // Beginning of current month
};

const getInitEndDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth() + 2, 0).toISOString().split("T")[0]; // End of next month
};

const filtersToInclude = [IssueFieldEnum.TAGS, IssueFieldEnum.PRIORITY, IssueFieldEnum.STATUS];

const emptyFilters: Record<IssueFieldEnum, string[]> = Object.values(filtersToInclude).reduce((acc, key) => {
  acc[key] = [];
  return acc;
}, {} as Record<IssueFieldEnum, string[]>);

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && Array.isArray(value) === false;
};

const normalizeStoredIssueColumnConfig = (value: unknown): IStoredIssueColConfigs | null => {
  if (!isRecord(value)) {
    return null;
  }

  const { colId, orderPriority, width } = value;
  if (colId != null && typeof colId !== "string") {
    return null;
  }

  if (orderPriority != null && (typeof orderPriority !== "number" || Number.isFinite(orderPriority) === false)) {
    return null;
  }

  if (width != null && (typeof width !== "number" || Number.isFinite(width) === false)) {
    return null;
  }

  if (colId == null && orderPriority == null && width == null) {
    return null;
  }

  const normalizedConfig: IStoredIssueColConfigs = {};
  if (typeof colId === "string") {
    normalizedConfig.colId = colId;
  }
  if (typeof orderPriority === "number") {
    normalizedConfig.orderPriority = orderPriority;
  }
  if (typeof width === "number") {
    normalizedConfig.width = width;
  }

  return normalizedConfig;
};

const normalizeAllIssueColumnState = (value: unknown): IAllIssueColumnStates => {
  if (!isRecord(value)) {
    return {};
  }

  return Object.entries(value).reduce<IAllIssueColumnStates>((acc, [issueBoardId, columnState]) => {
    if (Array.isArray(columnState) === false) {
      return acc;
    }

    const normalizedColumnState: IStoredIssueColConfigs[] = [];
    for (const columnConfig of columnState) {
      const normalizedColumnConfig = normalizeStoredIssueColumnConfig(columnConfig);
      if (normalizedColumnConfig == null) {
        return acc;
      }

      normalizedColumnState.push(normalizedColumnConfig);
    }

    acc[issueBoardId] = normalizedColumnState;
    return acc;
  }, {});
};

export const getAllIssueColumnStateFromLocalStorage = (): IAllIssueColumnStates => {
  try {
    return normalizeAllIssueColumnState(JSON.parse(localStorage.getItem(ISSUE_COLUMN_LS_KEY) || "{}"));
  } catch {
    return {};
  }
};

export const getIssueColumnStateFromLocalStorage = (
  issueBoardId?: string | null,
): IStoredIssueColConfigs[] | undefined => {
  if (issueBoardId == null) {
    return undefined;
  }

  return getAllIssueColumnStateFromLocalStorage()[issueBoardId];
};

export const persistIssueColumnStateInLocalStorage = (
  issueBoardId: string | null,
  columnState: IStoredIssueColConfigs[] | null,
): IAllIssueColumnStates | null => {
  if (issueBoardId == null) {
    return null;
  }

  const currentAllColumnState = getAllIssueColumnStateFromLocalStorage();
  const currentColumnState = currentAllColumnState[issueBoardId];

  if (columnState == null && currentColumnState == null) {
    return null;
  }

  if (isDeepEqual(currentColumnState, columnState)) {
    return null;
  }

  const updatedData = {
    ...currentAllColumnState,
    [issueBoardId]: columnState,
  };
  if (columnState === null) {
    delete updatedData[issueBoardId];
  }

  localStorage.setItem(ISSUE_COLUMN_LS_KEY, JSON.stringify(updatedData));
  return updatedData;
};

const initialState: IssueReducer = {
  allFiltersIssueBoardId: null,
  selectedPnsFilterItem: null,
  selectedPnsBoardFilterItem: null,
  activeTableTemplateId: null,
  activeView: (localStorage.getItem(ISSUE_VIEW_LS_KEY) as IssueViewEnum) || IssueViewEnum.TABLE,
  kanbanGroupingView: IssueKanbanGroupingViewEnum.STATUS,
  selectedGisMap: null,
  allColumnState: getAllIssueColumnStateFromLocalStorage(),
  sortedCol: null,
  activeDashboard: null,
  currentIssueBoardId: null,
  selectedIssueIdsByBoardId: {},
  svarGant: {
    zoomLevel: "week",
    startDate: localStorage.getItem("svarGantStartDate") || getInitStartDate(),
    endDate: localStorage.getItem("svarGantEndDate") || getInitEndDate(),
    skipItemsWithoutEndDate: false,
  },
  dropdownFilters: {} as Record<IssueFieldEnum, string[]>,
  miniSearchFreeText: "",
  issueAskAiPanelOpen: false,
  taskAndOrIssueArgs: ["ISSUE"],
  includeAllCompletedIssuesOnBoard: false,
  issueLogsCreatedAtRange: [null, null],
  issueLogsPageSize: 500,
  issueLogsCurrentPage: 1,
  mathFormulaConfigDraft: null,
  unlockedConnectedTaskCellKeys: [],
  formulaCounterGroupIssuesModal: null,
};

export const setIssueAllFiltersIfNoActiveTemplateThunk =
  (filters: IAllFilter[]): AppThunk =>
  (dispatch: any, getState) => {
    const { activeTableTemplateId } = getState().issueReducerNew;
    if (activeTableTemplateId == null) {
      // dispatch(setIssueAllFiltersInRedux(filters));
    }
  };

export const updateColumnStateThunk = (columnState: IStoredIssueColConfigs[] | null): AppThunk => {
  return (dispatch, getState) => {
    const issueBoardId = getState().issueReducerNew.currentIssueBoardId;
    const updatedData = persistIssueColumnStateInLocalStorage(issueBoardId, columnState);
    if (updatedData == null) return;

    dispatch(setIssueColumnStateInRedux(updatedData));
  };
};
export const issueSlice = createSlice({
  name: "issue",
  initialState,
  reducers: {
    setIssueActiveTableTemplateIdInRedux: (state, action: PayloadAction<string | null>) => {
      state.activeTableTemplateId = action.payload;
    },
    setIssueActiveViewInRedux: (state, action: PayloadAction<IIssueViewEnum>) => {
      localStorage.setItem(ISSUE_VIEW_LS_KEY, action.payload);
      state.activeView = action.payload;
    },
    setKanbanGroupingViewInRedux: (state, action: PayloadAction<IssueKanbanGroupingViewEnum>) => {
      state.kanbanGroupingView = action.payload;
    },
    resetIssueRedux: (state) => {
      state.activeTableTemplateId = null;
      state.kanbanGroupingView = IssueKanbanGroupingViewEnum.STATUS;
    },
    setIssueSelectedGisMapInRedux: (state, action: PayloadAction<ISelectedGisMap>) => {
      state.selectedGisMap = action.payload;
    },
    setIssueColumnStateInRedux: (state, action: PayloadAction<IAllIssueColumnStates>) => {
      state.allColumnState = action.payload;
    },
    setSelectedPnsFilterItemInRedux: (state, action: PayloadAction<string>) => {
      state.selectedPnsFilterItem = action.payload;
    },
    setIssueSortedColInRedux: (state, action: PayloadAction<ISortColumn>) => {
      state.sortedCol = action.payload;
    },
    setActiveDashboardInRedux: (state, action: PayloadAction<string>) => {
      state.activeDashboard = action.payload;
    },
    setIssueSelectedPnsBoardFilterItemInRedux: (state, action: PayloadAction<string>) => {
      state.selectedPnsBoardFilterItem = action.payload;
    },
    setCurrentIssueboardId: (state, action: PayloadAction<string | null>) => {
      if (state.currentIssueBoardId !== action.payload) {
        state.includeAllCompletedIssuesOnBoard = false;
      }
      state.currentIssueBoardId = action.payload;
    },
    setSelectedIssueIdsInRedux: (state, action: PayloadAction<{ issueBoardId: string; issueIds: string[] }>) => {
      state.selectedIssueIdsByBoardId[action.payload.issueBoardId] = Array.from(new Set(action.payload.issueIds));
    },
    addSelectedIssueIdsInRedux: (state, action: PayloadAction<{ issueBoardId: string; issueIds: string[] }>) => {
      const currentIssueIds = state.selectedIssueIdsByBoardId[action.payload.issueBoardId] ?? [];
      state.selectedIssueIdsByBoardId[action.payload.issueBoardId] = Array.from(
        new Set([...currentIssueIds, ...action.payload.issueIds]),
      );
    },
    removeSelectedIssueIdsInRedux: (state, action: PayloadAction<{ issueBoardId: string; issueIds: string[] }>) => {
      const issueIdsToRemove = new Set(action.payload.issueIds);
      state.selectedIssueIdsByBoardId[action.payload.issueBoardId] = (
        state.selectedIssueIdsByBoardId[action.payload.issueBoardId] ?? []
      ).filter((issueId) => !issueIdsToRemove.has(issueId));
    },
    toggleSelectedIssueIdInRedux: (state, action: PayloadAction<{ issueBoardId: string; issueId: string }>) => {
      const currentIssueIds = state.selectedIssueIdsByBoardId[action.payload.issueBoardId] ?? [];
      if (currentIssueIds.includes(action.payload.issueId)) {
        state.selectedIssueIdsByBoardId[action.payload.issueBoardId] = currentIssueIds.filter(
          (issueId) => issueId !== action.payload.issueId,
        );
        return;
      }

      state.selectedIssueIdsByBoardId[action.payload.issueBoardId] = [...currentIssueIds, action.payload.issueId];
    },
    clearSelectedIssueIdsInRedux: (state, action: PayloadAction<{ issueBoardId?: string } | undefined>) => {
      const issueBoardId = action.payload?.issueBoardId ?? state.currentIssueBoardId;
      if (issueBoardId == null) {
        return;
      }

      delete state.selectedIssueIdsByBoardId[issueBoardId];
    },
    setAllFiltersIssueBoardId: (state, action: PayloadAction<string | null>) => {
      state.allFiltersIssueBoardId = action.payload;
    },
    setSvarGantZoomLevelInRedux: (state, action: PayloadAction<"week" | "month">) => {
      state.svarGant.zoomLevel = action.payload;
    },
    setSvarGantStartDateInRedux: (state, action: PayloadAction<string | null>) => {
      const newStartDate = action.payload;
      state.svarGant.startDate = newStartDate;
      localStorage.setItem("svarGantStartDate", newStartDate || "");
      if (!newStartDate) {
        state.svarGant.startDate = localStorage.getItem("svarGantStartDate") || getInitStartDate(); // Default to beginning of current month
      }
    },
    setSvarGantEndDateInRedux: (state, action: PayloadAction<string | null>) => {
      const newEndDate = action.payload;
      state.svarGant.endDate = newEndDate;
      localStorage.setItem("svarGantEndDate", newEndDate || "");
      if (!newEndDate) {
        state.svarGant.endDate = localStorage.getItem("svarGantEndDate") || getInitEndDate(); // Default to end of next month
      }
    },
    setSvarGantSkipItemsWithoutEndDateInRedux: (state, action: PayloadAction<boolean>) => {
      state.svarGant.skipItemsWithoutEndDate = action.payload;
    },
    setIssueDropdownSingleDataFieldFilterInRedux: (
      state,
      action: PayloadAction<{ filter: IssueFieldEnum; values: string[] }>,
    ) => {
      const { filter, values } = action.payload;
      state.dropdownFilters[filter] = values;
    },
    setIssueDropdownFilterAllInRedux: (state, action: PayloadAction<{ filters: IIssueDropdownFilters }>) => {
      state.dropdownFilters = action.payload.filters;
    },
    clearAllDropdownFilterInRedux: (state) => {
      state.dropdownFilters = {};
    },
    setMiniSearchFreeTextInRedux: (state, action: PayloadAction<string>) => {
      state.miniSearchFreeText = action.payload;
    },
    setIssueAskAiPanelOpenInRedux: (state, action: PayloadAction<boolean>) => {
      state.issueAskAiPanelOpen = action.payload;
    },
    setTaskAndOrIssueArgsInRedux: (state, action: PayloadAction<("TASK" | "ISSUE")[]>) => {
      state.taskAndOrIssueArgs = action.payload;
    },
    setIncludeAllCompletedIssuesOnBoardInRedux: (state, action: PayloadAction<boolean>) => {
      state.includeAllCompletedIssuesOnBoard = action.payload;
    },
    //Historik-tabben
    setIssueLogsCreatedAtRangeInRedux: (state, action: PayloadAction<[string | null, string | null]>) => {
      state.issueLogsCreatedAtRange = action.payload;
    },
    setIssueLogsPageSizeInRedux: (state, action: PayloadAction<number>) => {
      state.issueLogsPageSize = action.payload;
    },
    setIssueLogsCurrentPageInRedux: (state, action: PayloadAction<number>) => {
      state.issueLogsCurrentPage = action.payload;
    },
    setMathFormulaConfigDraftInRedux: (state, action: PayloadAction<IOtherAttributesMathFormula | null>) => {
      state.mathFormulaConfigDraft = action.payload;
    },
    resetMathFormulaConfigInRedux: (state) => {
      state.mathFormulaConfigDraft = null;
    },
    unlockConnectedTaskCellInRedux: (state, action: PayloadAction<string>) => {
      if (state.unlockedConnectedTaskCellKeys.includes(action.payload) === false) {
        state.unlockedConnectedTaskCellKeys.push(action.payload);
      }
    },
    lockConnectedTaskCellInRedux: (state, action: PayloadAction<string>) => {
      state.unlockedConnectedTaskCellKeys = state.unlockedConnectedTaskCellKeys.filter((key) => key !== action.payload);
    },
    setFormulaCounterGroupIssuesModalInRedux: (
      state,
      action: PayloadAction<{ issueId: string; formulaAttributeKey: ICapitalLetter } | null>,
    ) => {
      state.formulaCounterGroupIssuesModal = action.payload;
    },
  },
});

export const thunkOpenIssueModalById =
  (issueId: string): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const args = selectGetOpenIssueArgsModalSelector(state, issueId);
    if (args.projectId == null || args.boardId == null) {
      console.warn("Kan ikke åpne issue modal – mangler projectId eller boardId");
      return;
    }
    dispatch(addAppModalThunk(args));
  };

export const {
  setIssueActiveTableTemplateIdInRedux,
  setIssueActiveViewInRedux,
  setKanbanGroupingViewInRedux,
  resetIssueRedux,
  setIssueSelectedGisMapInRedux,
  setSelectedPnsFilterItemInRedux,
  setIssueSortedColInRedux,
  setActiveDashboardInRedux,
  setIssueColumnStateInRedux,
  setIssueSelectedPnsBoardFilterItemInRedux,
  setCurrentIssueboardId,
  setSelectedIssueIdsInRedux,
  addSelectedIssueIdsInRedux,
  removeSelectedIssueIdsInRedux,
  toggleSelectedIssueIdInRedux,
  clearSelectedIssueIdsInRedux,
  setAllFiltersIssueBoardId,
  setSvarGantZoomLevelInRedux,
  setSvarGantStartDateInRedux,
  setSvarGantEndDateInRedux,
  setSvarGantSkipItemsWithoutEndDateInRedux,
  setIssueDropdownSingleDataFieldFilterInRedux,
  setMiniSearchFreeTextInRedux,
  setIssueAskAiPanelOpenInRedux,
  setTaskAndOrIssueArgsInRedux,
  setIncludeAllCompletedIssuesOnBoardInRedux,
  clearAllDropdownFilterInRedux,
  setIssueDropdownFilterAllInRedux,
  setIssueLogsCreatedAtRangeInRedux,
  setIssueLogsPageSizeInRedux,
  setIssueLogsCurrentPageInRedux,
  setMathFormulaConfigDraftInRedux,
  resetMathFormulaConfigInRedux,
  unlockConnectedTaskCellInRedux,
  lockConnectedTaskCellInRedux,
  setFormulaCounterGroupIssuesModalInRedux,
} = issueSlice.actions;

export default issueSlice.reducer;
